import { cleanRut } from '@smuchile/pkg-unimarc-components/utils'
import {
  getGuestUserMembership,
  sleep,
  trigger,
  getClientIP
} from '@smuchile/pkg-unimarc-hooks'
import { SetStateAction, useCallback, useRef, useState } from 'react'
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3'
import { getContentfulMembershipLevelBanner } from 'shared/contentful/getContentfulMembershipLevelBanner'
import { MembershipUnipayBanner } from 'shared/interfaces/MembershipUnipayBanner'

interface UseQueryMembershipLevel {
  showModalMembershipLevelQuery: boolean
  blockedBySecurity: boolean
  showModalMembershipType: boolean
  membershipType: string
  showModalRegisterClub: boolean
  isLoading: boolean
  unipayBannersCF: MembershipUnipayBanner[]
  unipayStatus: string
  setShowModalMembershipLevelQuery: React.Dispatch<SetStateAction<boolean>>
  handleToggleMembershipLevelQuery: () => void
  handleMembershipLevelQueryContinue: (rut: string, email?: string) => void
  handleToggleMembershipType: () => void
  handleToggleRegisterClub: () => void
}

/**
 * Custom hook to manage the query of membership levels.
 *
 * @returns {UseQueryMembershipLevel} - An object containing states and handlers for membership level queries.
 *
 * @example
 * ```typescript
 * const {
 *   showModalMembershipLevelQuery,
 *   blockedBySecurity,
 *   showModalMembershipType,
 *   membershipType,
 *   showModalRegisterClub,
 *   setShowModalMembershipLevelQuery,
 *   handleToggleMembershipLevelQuery,
 *   handleMembershipLevelQueryContinue,
 *   handleToggleMembershipType,
 *   handleToggleRegisterClub
 * } = useQueryMembershipLevel();
 * ```
 */
export const useQueryMembershipLevel = (): UseQueryMembershipLevel => {
  // invoke recaptcha
  const { executeRecaptcha } = useGoogleReCaptcha()
  // membership level query
  const [showModalMembershipLevelQuery, setShowModalMembershipLevelQuery] =
    useState(false)
  // security block query level button
  const [blockedBySecurity, setBlockedBySecurity] = useState(false)
  const blockBySecurityCountRef = useRef(0)
  // membership type
  const [showModalMembershipType, setShowModalMembershipType] = useState(false)
  const [membershipType, setMembershipType] = useState('')
  // register club
  const [showModalRegisterClub, setShowModalRegisterClub] = useState(false)
  // is loading
  const [isLoading, setIsLoading] = useState(false)
  // unipay
  const [unipayStatus, setUnipayStatus] = useState(null)
  const [unipayBannersCF, setUnipayBannersCF] = useState([])

  const handleToggleMembershipLevelQuery = () => {
    setShowModalMembershipLevelQuery(!showModalMembershipLevelQuery)
  }

  const handleMembershipLevelQueryContinue = useCallback(
    async (rut: string, email?: string) => {
      // execute recaptcha
      const token = await executeRecaptcha('identity_lookup')
      // start loading scenario
      setIsLoading(true)
      // prepare to block button by security
      if (blockedBySecurity) {
        setIsLoading(false)
        trigger({
          eventType: 'membershipToast',
          data: {
            show: true,
            message:
              'Has alcanzado el límite de consultas con tu Rut. Intenta más tarde.',
            errorCode: 'retries'
          }
        })
        setBlockedBySecurity(true)
        await sleep(10000)
        setBlockedBySecurity(false)
        blockBySecurityCountRef.current = 0
        return
      }
      blockBySecurityCountRef.current = blockBySecurityCountRef.current + 1

      if (blockBySecurityCountRef.current == 3) {
        setIsLoading(false)
        trigger({
          eventType: 'membershipToast',
          data: {
            show: true,
            message:
              'Has alcanzado el límite de consultas con tu Rut. Intenta más tarde.',
            errorCode: 'retries'
          }
        })
        setBlockedBySecurity(true)
        await sleep(10000)
        setBlockedBySecurity(false)
        blockBySecurityCountRef.current = 0
        return
      }
      // query the user level
      const clientIp = await getClientIP()
      const cleanedDocument = cleanRut(rut)
      const queryMembershipUser = await getGuestUserMembership({
        requestOptions: {
          headers: {
            'g-recaptcha-response': token,
            ip: clientIp.ip
          }
        },
        document: cleanedDocument,
        email
      })
      setIsLoading(false)
      const suscriptionType = queryMembershipUser?.userInfo?.level
      // if error status 429 too many requests show toast
      if (queryMembershipUser?.status === 429) {
        trigger({
          eventType: 'membershipToast',
          data: {
            show: true,
            message:
              'Has alcanzado el límite de consultas con tu Rut. Intenta más tarde.',
            errorCode: queryMembershipUser?.code || 'unknown'
          }
        })
        return
      }
      // if error status 400 or 500 show toast
      if (
        queryMembershipUser?.status === 400 ||
        queryMembershipUser?.status >= 500
      ) {
        trigger({
          eventType: 'membershipToast',
          data: {
            show: true,
            message:
              'Lo sentimos, no podemos mostrar la información. Intenta de nuevo.'
          }
        })
        return
      }
      if (queryMembershipUser?.error) {
        setShowModalMembershipLevelQuery(false)
        setShowModalRegisterClub(true)
      }
      if (suscriptionType) {
        setMembershipType(suscriptionType)
        setShowModalMembershipLevelQuery(false)
        setShowModalMembershipType(true)
      }
      // unipay banner call
      const unipayBannersCF = await getContentfulMembershipLevelBanner()
      setUnipayBannersCF(unipayBannersCF)
      if (queryMembershipUser?.userInfo?.preapprovedUnipayCard) {
        setUnipayStatus('preapprovedUnipayCard')
      }
      if (queryMembershipUser?.userInfo?.isUnipayCardHolder) {
        setUnipayStatus('isUnipayCardHolder')
      }
    },
    [blockedBySecurity, executeRecaptcha]
  )

  const handleToggleMembershipType = () => {
    setShowModalMembershipType(!showModalMembershipType)
  }

  const handleToggleRegisterClub = () => {
    setShowModalRegisterClub(!showModalRegisterClub)
  }

  return {
    showModalMembershipLevelQuery,
    blockedBySecurity,
    showModalMembershipType,
    membershipType,
    showModalRegisterClub,
    isLoading,
    unipayStatus,
    unipayBannersCF,
    setShowModalMembershipLevelQuery,
    handleToggleMembershipLevelQuery,
    handleMembershipLevelQueryContinue,
    handleToggleMembershipType,
    handleToggleRegisterClub
  }
}
